import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Tooltip } from "@mui/material";
import { useEffect, useState } from "react";
import Input from "../../components/Input/Input";
import MuiSwitch from "../../components/MuiSwitch/MuiSwitch";
import {
  createEmptyInterpretation,
  createEmptyInterpretationBand,
  duplicateInterpretation,
  getInterpretationBandErrors,
  getInterpretationCoverageErrors,
  getInterpretationBandPreview,
  getInterpretationOutputRange,
  getTextForLanguage,
  moveInterpretationBand,
  QuizFormInterpretation,
  QuizFormQuestion,
  setTextForLanguage,
} from "./systemQuizModel";
import InterpretationFormulaEditor from "./InterpretationFormulaEditor";
import s from "./SystemQuizDesigner.module.css";

type InterpretationDimensionsEditorProps = {
  interpretations: QuizFormInterpretation[];
  questions: QuizFormQuestion[];
  languages: string[];
  selectedLanguage: string;
  onChange: (interpretations: QuizFormInterpretation[]) => void;
};

const setMembership = (values: Set<string>, id: string, included: boolean) => {
  const next = new Set(values);
  if (included) {
    next.add(id);
  } else {
    next.delete(id);
  }
  return next;
};

const InterpretationDimensionsEditor = ({
  interpretations,
  questions,
  languages,
  selectedLanguage,
  onChange,
}: InterpretationDimensionsEditorProps) => {
  const [openLowerBoundIds, setOpenLowerBoundIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [openUpperBoundIds, setOpenUpperBoundIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [seenLowerBoundIds, setSeenLowerBoundIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [seenUpperBoundIds, setSeenUpperBoundIds] = useState<Set<string>>(
    () => new Set(),
  );

  useEffect(() => {
    const lowerToSee: string[] = [];
    const upperToSee: string[] = [];
    const lowerToOpen: string[] = [];
    const upperToOpen: string[] = [];

    interpretations.forEach((interpretation) => {
      interpretation.bands.forEach((band, bandIndex) => {
        const isFirst = bandIndex === 0;
        const isLast = bandIndex === interpretation.bands.length - 1;

        if (isFirst && !seenLowerBoundIds.has(band.id)) {
          lowerToSee.push(band.id);
          if (band.range.from === "") lowerToOpen.push(band.id);
        }

        if (isLast && !seenUpperBoundIds.has(band.id)) {
          upperToSee.push(band.id);
          if (band.range.to === "") upperToOpen.push(band.id);
        }
      });
    });

    if (lowerToSee.length) {
      setSeenLowerBoundIds((current) => {
        const next = new Set(current);
        lowerToSee.forEach((id) => next.add(id));
        return next;
      });
    }

    if (upperToSee.length) {
      setSeenUpperBoundIds((current) => {
        const next = new Set(current);
        upperToSee.forEach((id) => next.add(id));
        return next;
      });
    }

    if (lowerToOpen.length) {
      setOpenLowerBoundIds((current) => {
        const next = new Set(current);
        lowerToOpen.forEach((id) => next.add(id));
        return next;
      });
    }

    if (upperToOpen.length) {
      setOpenUpperBoundIds((current) => {
        const next = new Set(current);
        upperToOpen.forEach((id) => next.add(id));
        return next;
      });
    }
  }, [interpretations, seenLowerBoundIds, seenUpperBoundIds]);

  const updateDimension = (
    dimensionIndex: number,
    updater: (dimension: QuizFormInterpretation) => QuizFormInterpretation,
  ) =>
    onChange(
      interpretations.map((dimension, index) =>
        index === dimensionIndex ? updater(dimension) : dimension,
      ),
    );

  const updateBand = (
    dimensionIndex: number,
    bandIndex: number,
    updater: (
      band: QuizFormInterpretation["bands"][number],
    ) => QuizFormInterpretation["bands"][number],
  ) =>
    updateDimension(dimensionIndex, (dimension) => ({
      ...dimension,
      bands: dimension.bands.map((band, index) =>
        index === bandIndex ? updater(band) : band,
      ),
    }));

  return (
    <section className={s.section}>
      <div className={s.sectionHeader}>
        <div>
          <h2 className={s.sectionTitle}>Interpretation dimensions</h2>
          <p className={s.sectionHint}>
            Each dimension calculates its own named result from the visible
            Q1, Q2, and other positional question references.
          </p>
        </div>
        <button
          type="button"
          className={s.primaryActionButton}
          onClick={() =>
            onChange([...interpretations, createEmptyInterpretation(languages)])
          }
        >
          <AddIcon fontSize="small" />
          Add Dimension
        </button>
      </div>

      <div className={s.interpretationList}>
        {interpretations.map((interpretation, dimensionIndex) => {
          const bandErrors = getInterpretationBandErrors(interpretation.bands);
          const outputRange = getInterpretationOutputRange(
            interpretation.formula,
            questions,
          );
          const coverageErrors = outputRange.valid
            ? getInterpretationCoverageErrors(
                interpretation.bands,
                outputRange,
              )
            : [];

          return (
            <article className={s.interpretationCard} key={interpretation.id}>
              <div className={s.interpretationHeader}>
                <div>
                  <span className={s.maximumLabel}>
                    Dimension {dimensionIndex + 1}
                  </span>
                  <strong>
                    {getTextForLanguage(
                      interpretation.title,
                      selectedLanguage,
                    ) || "Untitled interpretation"}
                  </strong>
                </div>
                <div className={s.bandActions}>
                  <Tooltip title="Duplicate dimension" arrow>
                    <button
                      type="button"
                      className={s.iconButton}
                      aria-label={`Duplicate dimension ${dimensionIndex + 1}`}
                      onClick={() =>
                        onChange([
                          ...interpretations.slice(0, dimensionIndex + 1),
                          duplicateInterpretation(interpretation),
                          ...interpretations.slice(dimensionIndex + 1),
                        ])
                      }
                    >
                      <ContentCopyIcon fontSize="small" />
                    </button>
                  </Tooltip>
                  <Tooltip title="Delete dimension" arrow>
                    <span>
                      <button
                        type="button"
                        className={s.iconButton}
                        disabled={interpretations.length <= 1}
                        aria-label={`Delete dimension ${dimensionIndex + 1}`}
                        onClick={() =>
                          onChange(
                            interpretations.filter(
                              (_, index) => index !== dimensionIndex,
                            ),
                          )
                        }
                      >
                        <DeleteIcon fontSize="small" />
                      </button>
                    </span>
                  </Tooltip>
                </div>
              </div>

              {selectedLanguage && (
                <Input
                  lable={
                    dimensionIndex === 0
                      ? `Interpretation Title (${selectedLanguage})`
                      : `Interpretation ${dimensionIndex + 1} Title (${selectedLanguage})`
                  }
                  inputValue={getTextForLanguage(
                    interpretation.title,
                    selectedLanguage,
                  )}
                  onChangeInput={(value) =>
                    updateDimension(dimensionIndex, (dimension) => ({
                      ...dimension,
                      title: setTextForLanguage(
                        dimension.title,
                        selectedLanguage,
                        value,
                      ),
                    }))
                  }
                />
              )}

              <InterpretationFormulaEditor
                value={interpretation.formula}
                onChange={(formula) =>
                  updateDimension(dimensionIndex, (dimension) => ({
                    ...dimension,
                    formula,
                  }))
                }
                questions={questions}
                selectedLanguage={selectedLanguage}
                inputId={`interpretation-formula-${dimensionIndex + 1}`}
                inputLabel={
                  dimensionIndex === 0
                    ? "Interpretation Formula"
                    : `Interpretation ${dimensionIndex + 1} Formula`
                }
              />

              <div className={s.bandSectionHeader}>
                <div>
                  <span className={s.fieldLabel}>Ordered result bands</span>
                  <span className={s.helperText}>
                    Bands must be contiguous. Only the first may be open below
                    and only the last may be open above.
                    {outputRange.valid
                      ? ` This formula can produce rounded results from ${outputRange.min} to ${outputRange.max}.`
                      : ""}
                  </span>
                </div>
                <button
                  type="button"
                  className={s.secondaryButton}
                  onClick={() => {
                    const previous =
                      interpretation.bands[interpretation.bands.length - 1];
                    const previousTo = Number(previous?.range.to);
                    const suggestedFrom =
                      previous?.range.to.trim() && Number.isFinite(previousTo)
                        ? String(Math.floor(previousTo) + 1)
                        : "";
                    updateDimension(dimensionIndex, (dimension) => ({
                      ...dimension,
                      bands: [
                        ...dimension.bands,
                        createEmptyInterpretationBand(
                          languages,
                          suggestedFrom,
                          "",
                        ),
                      ],
                    }));
                  }}
                >
                  <AddIcon fontSize="small" />
                  Add Band
                </button>
              </div>

              {coverageErrors.length > 0 && (
                <ul className={s.inlineErrorList}>
                  {coverageErrors.map((error) => (
                    <li key={error}>{error}</li>
                  ))}
                </ul>
              )}

              <div className={s.bandList}>
                {interpretation.bands.map((band, bandIndex) => {
                  const isFirst = bandIndex === 0;
                  const isLast = bandIndex === interpretation.bands.length - 1;
                  const from = Number(band.range.from);

                  return (
                    <div className={s.resultBandCard} key={band.id}>
                      <div className={s.bandCardHeader}>
                        <div>
                          <span className={s.fieldLabel}>
                            Band {bandIndex + 1}
                          </span>
                          <strong>{getInterpretationBandPreview(band)}</strong>
                        </div>
                        <div className={s.bandActions}>
                          <Tooltip title="Move band up" arrow>
                            <span>
                              <button
                                type="button"
                                className={s.iconButton}
                                disabled={isFirst}
                                aria-label={`Move band ${bandIndex + 1} up`}
                                onClick={() =>
                                  updateDimension(
                                    dimensionIndex,
                                    (dimension) => ({
                                      ...dimension,
                                      bands: moveInterpretationBand(
                                        dimension.bands,
                                        bandIndex,
                                        -1,
                                      ),
                                    }),
                                  )
                                }
                              >
                                <KeyboardArrowUpIcon fontSize="small" />
                              </button>
                            </span>
                          </Tooltip>
                          <Tooltip title="Move band down" arrow>
                            <span>
                              <button
                                type="button"
                                className={s.iconButton}
                                disabled={isLast}
                                aria-label={`Move band ${bandIndex + 1} down`}
                                onClick={() =>
                                  updateDimension(
                                    dimensionIndex,
                                    (dimension) => ({
                                      ...dimension,
                                      bands: moveInterpretationBand(
                                        dimension.bands,
                                        bandIndex,
                                        1,
                                      ),
                                    }),
                                  )
                                }
                              >
                                <KeyboardArrowDownIcon fontSize="small" />
                              </button>
                            </span>
                          </Tooltip>
                          <Tooltip title="Delete band" arrow>
                            <span>
                              <button
                                type="button"
                                className={s.iconButton}
                                disabled={interpretation.bands.length <= 1}
                                aria-label={`Delete band ${bandIndex + 1}`}
                                onClick={() =>
                                  updateDimension(
                                    dimensionIndex,
                                    (dimension) => ({
                                      ...dimension,
                                      bands: dimension.bands.filter(
                                        (_, index) => index !== bandIndex,
                                      ),
                                    }),
                                  )
                                }
                              >
                                <DeleteIcon fontSize="small" />
                              </button>
                            </span>
                          </Tooltip>
                        </div>
                      </div>

                      <div className={s.openBounds}>
                        {isFirst && (
                          <MuiSwitch
                            lable="No lower bound"
                            checked={openLowerBoundIds.has(band.id)}
                            setChecked={(isOpen) => {
                              setOpenLowerBoundIds((current) =>
                                setMembership(current, band.id, isOpen),
                              );
                              updateBand(dimensionIndex, bandIndex, (current) => ({
                                ...current,
                                range: {
                                  ...current.range,
                                  from: isOpen
                                    ? ""
                                    : current.range.to || "0",
                                },
                              }));
                            }}
                          />
                        )}
                        {isLast && (
                          <MuiSwitch
                            lable="No upper bound"
                            checked={openUpperBoundIds.has(band.id)}
                            setChecked={(isOpen) => {
                              setOpenUpperBoundIds((current) =>
                                setMembership(current, band.id, isOpen),
                              );
                              updateBand(dimensionIndex, bandIndex, (current) => ({
                                ...current,
                                range: {
                                  ...current.range,
                                  to: isOpen
                                    ? ""
                                    : Number.isInteger(from)
                                      ? String(from)
                                      : "0",
                                },
                              }));
                            }}
                          />
                        )}
                      </div>

                      <div className={s.rangeInputs}>
                        <Input
                          lable={
                            dimensionIndex === 0 && bandIndex === 0
                              ? "Result Band From"
                              : `Dimension ${dimensionIndex + 1} Band ${bandIndex + 1} From`
                          }
                          type="number"
                          step="0.01"
                          disabled={isFirst && openLowerBoundIds.has(band.id)}
                          inputValue={band.range.from}
                          onChangeInput={(value) => {
                            setOpenLowerBoundIds((current) =>
                              setMembership(current, band.id, false),
                            );
                            updateBand(dimensionIndex, bandIndex, (current) => ({
                              ...current,
                              range: { ...current.range, from: value },
                            }));
                          }}
                        />
                        <Input
                          lable={
                            dimensionIndex === 0 && bandIndex === 0
                              ? "Result Band To"
                              : `Dimension ${dimensionIndex + 1} Band ${bandIndex + 1} To`
                          }
                          type="number"
                          step="0.01"
                          disabled={isLast && openUpperBoundIds.has(band.id)}
                          inputValue={band.range.to}
                          onChangeInput={(value) => {
                            setOpenUpperBoundIds((current) =>
                              setMembership(current, band.id, false),
                            );
                            updateBand(dimensionIndex, bandIndex, (current) => ({
                              ...current,
                              range: { ...current.range, to: value },
                            }));
                          }}
                        />
                      </div>

                      {selectedLanguage && (
                        <Input
                          lable={
                            dimensionIndex === 0 && bandIndex === 0
                              ? `Result Text (${selectedLanguage})`
                              : `Dimension ${dimensionIndex + 1} Band ${bandIndex + 1} Text (${selectedLanguage})`
                          }
                          inputValue={getTextForLanguage(
                            band.text,
                            selectedLanguage,
                          )}
                          onChangeInput={(value) =>
                            updateBand(dimensionIndex, bandIndex, (current) => ({
                              ...current,
                              text: setTextForLanguage(
                                current.text,
                                selectedLanguage,
                                value,
                              ),
                            }))
                          }
                        />
                      )}

                      {bandErrors[bandIndex].length > 0 && (
                        <ul className={s.inlineErrorList}>
                          {bandErrors[bandIndex].map((error) => (
                            <li key={error}>{error}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
};

export default InterpretationDimensionsEditor;
